import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button, Container, Typography } from "src/UILibrary"

import { useRecoilState } from "recoil"
import { useLocation, useNavigate } from "react-router-dom"
import { validateField } from "src/modules/validation"
import * as yup from "yup"
import { ShowToast } from "src/states/showToast"
import { AuthenticationService } from "src/openapi"
import { RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import { ShowHeader } from "src/states/showHeader"
import InputTextField from "src/components/atom/InputTextField"
import { ShowProgress } from "src/states/showProgress"

export const PasswordReset = () => {
  const { t } = useTranslation("translation", { keyPrefix: "password_reset" })

  const navigate = useNavigate()

  const [, setShowHeader] = useRecoilState(ShowHeader)
  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const { state } = useLocation()
  const { email } = state || ""

  if (!email) {
    navigate("/password_reset/email")
  }

  const [password, setPassword] = useState<string>("")
  const [passwordConfirm, setPasswordConfirm] = useState<string>("")
  const [passwordValidationMessage, setPasswordValidationMessage] = useState<string>("")
  const [passwordConfirmValidationMessage, setPasswordConfirmValidationMessage] =
    useState<string>("")
  const [code, setCode] = useState<string>("")
  const [codeValidationMessage, setCodeValidationMessage] = useState<string>("")

  useEffect(() => {
    setShowHeader(false)
  })

  useEffect(() => {
    setShowToast({ show: true, text: t("confirm_code_in_mail"), type: SNACKBAR_TYPES.INFORMATION })
  }, [state, setShowToast, t])

  const validate = async () => {
    let ret = true

    if (password != passwordConfirm) {
      setPasswordValidationMessage(t("password_password_confirm_different"))
      ret = ret && false
    } else {
      try {
        await validateField(
          yup.object().shape({
            password: yup
              .string()
              .required(t("please_input_password"))
              .min(8, t("password_length_min"))
              .max(20, t("password_length_max"))
              .test("isValidPassword", t("password_regex"), (value) => {
                const hasLowerCase = /[a-z]/.test(value)
                const hasNumber = /[0-9]/.test(value)

                return hasLowerCase && hasNumber
              }),
          }),
          "password",
          password
        )
        setPasswordValidationMessage("")
      } catch (err: any) {
        setPasswordValidationMessage(err.message)
        ret = ret && false
      }

      try {
        await validateField(
          yup.object().shape({
            passwordConfirm: yup
              .string()
              .required(t("please_input_password"))
              .min(8, t("password_length_min"))
              .max(20, t("password_length_max"))
              .test("isValidPassword", t("password_regex"), (value) => {
                const hasLowerCase = /[a-z]/.test(value)
                const hasNumber = /[0-9]/.test(value)

                return hasLowerCase && hasNumber
              }),
          }),
          "passwordConfirm",
          passwordConfirm
        )
        setPasswordConfirmValidationMessage("")
      } catch (err: any) {
        setPasswordConfirmValidationMessage(err.message)
        ret = ret && false
      }
    }

    try {
      await validateField(
        yup.object().shape({
          code: yup
            .string()
            .length(6, t("verify_code_length"))
            .matches(/^\d+$/, t("verify_code_number")),
        }),
        "code",
        code
      )
      setCodeValidationMessage("")
    } catch (err: any) {
      setCodeValidationMessage(err.message)
      ret = ret && false
    }

    return ret
  }

  const handlePasswordReset = async () => {
    if (!(await validate())) {
      return
    }

    setShowProgress(true)
    AuthenticationService.resetPassword({
      email: email,
      confirmationCode: code,
      password: password,
    })
      .then((res) => {
        setShowProgress(false)
        if (res.status === RESPONSE_CODE.SUCCESS) {
          navigate("/signin")
        } else {
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ display: "flex", flexDirection: "column" }}>
        <Typography.Large sx={{ mt: "100px", color: "text.primary" }}>
          {t("password_reset_confirm")}
        </Typography.Large>
        <Container maxWidth="md" sx={{ mx: "auto" }}>
          <Typography.DetailText sx={{ mt: "60px", color: "text.primary", fontWeight: "400" }}>
            {t("password_reset_confirm_description")}
          </Typography.DetailText>
          <InputTextField
            name={t("email")}
            text={email}
            disabled
            handleText={() => {}}
            sx={{ mt: "40px" }}
          />
          <InputTextField
            name={t("password")}
            password
            required
            text={password}
            handleText={setPassword}
            sx={{ mt: "40px" }}
            validationMessage={passwordValidationMessage}
          />
          <InputTextField
            required
            password
            name={t("password_confirm")}
            text={passwordConfirm}
            handleText={setPasswordConfirm}
            sx={{ mt: "40px" }}
            validationMessage={passwordConfirmValidationMessage}
          />
          <InputTextField
            required
            name={t("verification_code")}
            text={code}
            handleText={setCode}
            sx={{ mt: "40px", width: "150px" }}
            validationMessage={codeValidationMessage}
          />
          <Button
            variant="contained"
            sx={{
              width: "240px",
              py: "16px",
              fontSize: "1rem",
              fontFamily: "Noto Sans JP !important",
              lineHeight: "1rem",
              fontWeight: 700,
              my: "80px",
              display: "flex",
              justifyContent: "center",
              borderRadius: 0,
              ":hover": {
                bgcolor: "#2DA389",
              },
            }}
            onClick={() => handlePasswordReset()}
          >
            {t("reset")}
          </Button>
        </Container>
      </Container>
    </>
  )
}
